<template>
    <div>
        <template v-if="!isAdmin">
            <PermissionDenied />
        </template>
        <template v-else>
            <vs-tabs v-model="activeTab">
                <!-- List Tab -->
                <vs-tab label="Purchase Order Detail List" icon-pack="feather" icon="icon-list">
                    <div class="mt-6">
                        <!-- Action Buttons -->
                        <vs-row class="mb-4">
                            <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="text-right">
                                <div class="flex justify-end gap-2">
                                    <vs-button @click="activeTab = 1; resetForm();" color="primary" type="filled">
                                        <font-awesome-icon :icon="plusIcon" class="mr-2" /> Add New
                                    </vs-button>
                                    <vx-tooltip text="Refresh Data" color="primary">
                                        <vs-button color="primary" type="filled" @click="loadData" :disabled="loading">
                                            <font-awesome-icon :icon="redoIcon" />
                                        </vs-button>
                                    </vx-tooltip>
                                </div>
                            </vs-col>
                        </vs-row>
                        
                        <vx-card>
                            <div v-if="loading" class="text-center p-4">
                                <h3>Loading Data.. Please wait..</h3>
                            </div>
                            
                            <vs-table :data="results" slot="no-body" :hoverFlat="true" stripe v-if="!loading" noDataText="">
                                <vs-tr>
                                    <vs-th style="min-width: 80px;">S.No</vs-th>
                                    <vs-th style="min-width: 200px;">Item Sub Group Code</vs-th>
                                    <vs-th style="min-width: 80px;">Qty</vs-th>
                                    <vs-th style="min-width: 120px;">Unit rate</vs-th>
                                    <vs-th style="min-width: 80px;">GST %</vs-th>
                                    <vs-th style="min-width: 150px;">Amount</vs-th>
                                    <vs-th style="min-width: 100px;">Action</vs-th>
                                </vs-tr>
                                <template v-if="results.length > 0">
                                    <template v-for="(item, index) in results">
                                        <vs-tr :key="index">
                                            <vs-td>
                                                {{ filters.offset + index + 1 }}
                                            </vs-td>
                                            <vs-td>
                                                {{ (item.item_sub_group && item.item_sub_group.code) || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.qty || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.unit_rate || '-' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.gst_percent || '0' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ calculateAmount(item) }}
                                            </vs-td>
                                            <vs-td>
                                                 <div class="flex">
                                                    <vx-tooltip text="Edit Detail">
                                                        <vs-button 
                                                            @click="editItem(item)" 
                                                            color="warning" 
                                                            type="flat" 
                                                            class="p-2"
                                                        >
                                                            <font-awesome-icon :icon="editIcon" />
                                                        </vs-button>
                                                    </vx-tooltip>
                                                    <vx-tooltip text="Delete Detail" v-if="isActuallyAdmin">
                                                        <vs-button 
                                                            @click="confirmDelete(item)" 
                                                            color="danger" 
                                                            type="flat" 
                                                            class="p-2"
                                                        >
                                                            <font-awesome-icon :icon="deleteIcon" />
                                                        </vs-button>
                                                    </vx-tooltip>
                                                </div>
                                            </vs-td>
                                        </vs-tr>
                                    </template>
                                </template>
                                <template v-else>
                                    <vs-tr>
                                        <vs-td colspan="7" class="text-center p-4">
                                            <p class="text-muted mb-0">No data available</p>
                                        </vs-td>
                                    </vs-tr>
                                </template>
                            </vs-table>
                        </vx-card>
                        
                        <!-- Pagination -->
                        <template v-if="total_results">
                            <vs-row class="mt-3 text-center">
                                <vs-pagination :total="total_pages" v-model="current_page" goto class="mx-auto"></vs-pagination>
                            </vs-row>
                        </template>
                    </div>
                </vs-tab>

                <!-- Form Tab -->
                <vs-tab :label="editingId ? 'Edit PO Detail' : 'Add New Detail'" icon-pack="feather" icon="icon-edit">
                    <div class="mt-6">
                        <vx-card noShadow noRound>
                            <div class="mb-4">
                                <h3 class="mb-2">{{ editingId ? 'Edit Purchase Order Detail' : 'Create Purchase Order Detail' }}</h3>
                                <p class="text-muted mb-4" v-if="!editingId">Fill in the details below to create a new purchase order detail entry.</p>
                                <p class="text-muted mb-4" v-else>Update the purchase order detail below.</p>
                            </div>
                
                <vs-row>
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                        <SelectForeign
                            :model.sync="formData.purchase_order"
                            label="Purchase Order *"
                            placeholder="Type to Search..."
                            :multiple="false"
                            :load_initial="true"
                            :label_keys="['po_number']"
                            :show_id_in_label="false"
                            :endpoint="poEndpoint"
                            container_class="w-full"
                            input_class=""
                            :use_id_as_value="true"
                            :close_on_select="true"
                        />
                        <small class="block text-danger mt-1" v-if="api_errors.purchase_order">
                            {{ (api_errors.purchase_order || []).join(', ') }}
                        </small>
                    </vs-col>
                    
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                        <SelectForeign
                            :model.sync="formData.item_sub_group"
                            label="Item Sub Group Code *"
                            placeholder="Type to Search..."
                            :multiple="false"
                            :load_initial="true"
                            :label_keys="['code']"
                            :show_id_in_label="false"
                            :endpoint="itemSubGroupEndpoint"
                            container_class="w-full"
                            input_class=""
                            :use_id_as_value="true"
                            :close_on_select="true"
                        />
                        <small class="block text-danger mt-1" v-if="api_errors.item_sub_group">
                            {{ (api_errors.item_sub_group || []).join(', ') }}
                        </small>
                    </vs-col>

                    <vs-col vs-xs="12" vs-sm="6" vs-lg="3" class="mb-4">
                        <label class="vs-input--label block mb-1">Item Sub Group Description</label>
                        <vs-input
                            :value="itemSubGroupDescription || 'Not editable'"
                            class="w-full"
                            disabled
                        />
                    </vs-col>
                    
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="2" class="mb-4">
                        <vs-input
                            v-model.number="formData.qty"
                            label="Qty *"
                            type="number"
                            placeholder="e.g., 20"
                            class="w-full"
                        />
                        <small class="block text-danger mt-1" v-if="api_errors.qty">
                            {{ (api_errors.qty || []).join(', ') }}
                        </small>
                    </vs-col>
                    
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="2" class="mb-4">
                        <vs-input
                            v-model.number="formData.unit_rate"
                            label="Unit Rate *"
                            type="number"
                            step="0.01"
                            placeholder="e.g., 100.0"
                            class="w-full"
                        />
                        <small class="block text-danger mt-1" v-if="api_errors.unit_rate">
                            {{ (api_errors.unit_rate || []).join(', ') }}
                        </small>
                    </vs-col>
                    
                    <vs-col vs-xs="12" vs-sm="6" vs-lg="2" class="mb-4">
                        <vs-input
                            v-model.number="formData.gst_percent"
                            label="GST % *"
                            type="number"
                            step="0.01"
                            placeholder="e.g., 18"
                            class="w-full"
                        />
                        <small class="block text-danger mt-1" v-if="api_errors.gst_percent">
                            {{ (api_errors.gst_percent || []).join(', ') }}
                        </small>
                    </vs-col>
                </vs-row>
                
                <vs-row class="mt-4">
                    <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="text-right">
                        <vs-button @click="activeTab = 0; resetForm();" color="warning" type="flat" class="mr-2">
                            Cancel
                        </vs-button>
                        <vs-button @click="save" :disabled="submit_disabled" color="primary" type="filled">
                            {{ editingId ? 'Update' : 'Save' }}
                        </vs-button>
                        </vs-col>
                    </vs-row>
                </vx-card>
                    </div>
                </vs-tab>
            </vs-tabs>
        </template>
    </div>
</template>

<script>
import { faRedo, faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Request } from '../../utils/requests';
import SelectForeign from '../../components/letstream/ls-select-foreign/SelectForeign.vue';

export default {
    components: {
        SelectForeign
    },
    data() {
        let offset = parseInt(this.$route.query.offset || 0);
        let limit = parseInt(this.$route.query.limit || 20);
        
        let current_page = offset == 0 ? 1 : Math.ceil(offset / limit) + 1;
            return {
                results: [],
                loading: false,
                submit_disabled: false,
                api_errors: {},
                editingId: null,
                total_results: null,
            current_page: current_page,
            filters: {
                    offset: offset,
                    limit: limit
                },
                formData: {
                    purchase_order: null,
                    item_sub_group: null,
                    qty: null,
                    unit_rate: null,
                    gst_percent: null
                },
                itemSubGroupOptions: [],
                itemSubGroupDescription: '',
                activeTab: 0
            }
    },
    computed: {
        redoIcon: () => faRedo,
        plusIcon: () => faPlus,
        editIcon: () => faEdit,
        deleteIcon: () => faTrash,
        isActuallyAdmin() {
            if (this.$store.state.organisation.organisation_user && this.$store.state.organisation.organisation_user.role) {
                let role = this.$store.state.organisation.organisation_user.role.role.toLowerCase();
                return role == 'admin' || role == 'super admin';
            }
            return this.$store.state.auth.userRole == 'admin';
        },
        isAdmin() {
            return this.$store.state.auth.userRole == 'admin'
        },
        total_pages() {
            if(this.total_results == 0 || this.filters.limit == 0)  return 1
            return Math.ceil(this.total_results/this.filters.limit)
        },
        poEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.purchase_order}?organisation=${orgId}` : this.$store.state.apiendpoints.purchase_order;
        },
        itemSubGroupEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_item_sub_group}?organisation=${orgId}&is_active=true` : this.$store.state.apiendpoints.master_item_sub_group;
        }
    },
    watch: {
        filters: {
            deep: true,
            handler(newV) {
                if (this.current_page == null) return;

                if (this.timer) clearTimeout(this.timer);

                this.timer = setTimeout(() => {
                    this.$router.push({ 
                        name: this.$route.name, 
                        query: {
                            offset: newV.offset,
                            limit: newV.limit
                        } 
                    });
                }, 800);
            }
        },
        "$route.query"(newV) {
            this.loadData();
        },
        current_page(newV, oldV) {
            if (newV == oldV) return;
            this.filters.offset = (newV - 1) * this.filters.limit;
        },
        'formData.item_sub_group'(newVal) {
            if (!newVal) {
                this.itemSubGroupDescription = '';
                return;
            }
            const found = this.itemSubGroupOptions.find(opt => opt.id === newVal || opt.id === parseInt(newVal));
            this.itemSubGroupDescription = found ? (found.description || '') : '';
        }
    },
    mounted() {
        this.init()
        this.loadItemSubGroupOptions()
    },
    methods: {
        loadItemSubGroupOptions() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            let url = orgId
                ? `${this.$store.state.apiendpoints.master_item_sub_group}?organisation=${orgId}&is_active=true&limit=1000`
                : `${this.$store.state.apiendpoints.master_item_sub_group}?limit=1000`;
            Request(this, 'GET', url).then(res => {
                const list = (res && res.results) ? res.results : (Array.isArray(res) ? res : []);
                this.itemSubGroupOptions = list;
            });
        },
        loadData() {
            this.loading = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.purchase_order_detail;
            
            // Add organisation and pagination as query parameters
            let payload = {
                offset: this.filters.offset,
                limit: this.filters.limit
            }
            if (this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id) {
                payload.organisation = this.$store.state.organisation.organisation.id
            }
            
            Request(this, 'GET', url, payload).then(
                (res) => {
                    // Handle response structure: {count, results: [...]} or nested
                    if (res && res.results && Array.isArray(res.results)) {
                        // Standard paginated response: {count, results: [...]}
                        this.results = res.results
                        this.total_results = res.count || 0
                    } else if (res && res.data && Array.isArray(res.data)) {
                        // Response format: {data: [...], count: X}
                        this.results = res.data
                        this.total_results = res.count !== undefined ? res.count : res.data.length
                    } else if (res && res.data && res.data.results) {
                        // Direct array response
                        this.results = res
                        this.total_results = res.length
                    } else if (res && res.data && Array.isArray(res.data)) {
                        // Nested array: {data: [...]}
                        this.results = res.data
                        this.total_results = res.data.length
                    } else {
                        this.results = []
                        this.total_results = 0
                    }
                    this.calculate_page()
                    this.loading = false
                    this.$vs.loading.close()
                },
                (err) => {
                    this.loading = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An error occurred while loading data. Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                }
            )
        },
        save() {
            if (!this.formData.purchase_order || !this.formData.item_sub_group || !this.formData.qty || !this.formData.unit_rate || !this.formData.gst_percent) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Please fill all required fields!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            if (!this.$store.state.organisation.organisation || !this.$store.state.organisation.organisation.id) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Organisation not found!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            this.submit_disabled = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.purchase_order_detail
            let method = 'POST'
            
            if (this.editingId) {
                url += this.editingId + '/'
                method = 'PUT'
            }
            
            // Ensure we're sending IDs, not codes
            let payload = {
                purchase_order: parseInt(this.formData.purchase_order) || this.formData.purchase_order,
                item_sub_group: parseInt(this.formData.item_sub_group) || this.formData.item_sub_group,
                qty: this.formData.qty,
                unit_rate: this.formData.unit_rate,
                gst_percent: this.formData.gst_percent,
                organisation: this.$store.state.organisation.organisation.id
            }
            
            Request(this, method, url, null, payload).then(
                (res) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Success',
                        text: this.editingId ? "Updated Successfully!" : "Saved Successfully!",
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success'
                    })
                    this.activeTab = 0
                    this.resetForm()
                    this.loadData()
                },
                (err) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    if (err.error) {
                        this.api_errors = err.error
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "Please fix the errors and try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "An unknown error occurred! Please try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        },
        editItem(item) {
            this.editingId = item.id
            
            // Handle both ID and object for foreign keys
            const poId = (typeof item.purchase_order === 'object' && item.purchase_order) ? item.purchase_order.id : item.purchase_order
            const subGroupData = item.item_sub_group
            const subGroupId = (typeof subGroupData === 'object' && subGroupData) ? subGroupData.id : subGroupData
            
            this.formData = {
                purchase_order: poId,
                item_sub_group: subGroupId,
                qty: item.qty,
                unit_rate: item.unit_rate,
                gst_percent: item.gst_percent
            }

            // Auto-fill description from the item data or options list
            if (typeof subGroupData === 'object' && subGroupData && subGroupData.description) {
                this.itemSubGroupDescription = subGroupData.description;
            } else {
                const found = this.itemSubGroupOptions.find(opt => opt.id === subGroupId);
                this.itemSubGroupDescription = found ? (found.description || '') : '';
            }
            
            this.activeTab = 1
        },
        resetForm() {
            this.editingId = null
            this.formData = {
                purchase_order: null,
                item_sub_group: null,
                qty: null,
                unit_rate: null,
                gst_percent: null
            }
            this.api_errors = {}

            // Scroll to top
            if (this.$el) {
                this.$el.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        calculate_page() {
            if (this.filters.offset == 0) {
                this.current_page = 1;
            } else {
                this.current_page = Math.ceil(this.filters.offset / this.filters.limit) + 1;
            }
        },
        calculateAmount(item) {
            if (item.qty && item.unit_rate && item.gst_percent) {
                return (item.qty * item.unit_rate * (1 + item.gst_percent / 100)).toFixed(2);
            }
            return '0.00';
        },
        init() {
            // Initialize filters from route query
            let offset = parseInt(this.$route.query.offset || 0);
            let limit = parseInt(this.$route.query.limit || 20);
            this.filters.offset = offset;
            this.filters.limit = limit;
            this.calculate_page();
            
            // Set query params in URL if they don't exist
            if (!this.$route.query.offset || !this.$route.query.limit) {
                this.$router.replace({ 
                    name: this.$route.name, 
                    query: { 
                        ...this.$route.query,
                        offset: this.filters.offset, 
                        limit: this.filters.limit 
                    } 
                });
            }
            
            this.loadData();
        },
        confirmDelete(item) {
            this.$vs.dialog({
                type: 'confirm',
                color: 'danger',
                title: 'Confirm Delete',
                text: `Are you sure you want to delete this PO detail? This action cannot be undone.`,
                acceptText: 'Delete',
                cancelText: 'Cancel',
                accept: () => this.deleteItem(item)
            });
        },
        deleteItem(item) {
            this.$vs.loading();
            let url = `${this.$store.state.apiendpoints.purchase_order_detail}${item.id}/`;
            Request(this, 'DELETE', url).then(res => {
                this.$vs.loading.close();
                this.$vs.notify({
                    title: 'Success',
                    text: 'PO detail deleted successfully',
                    color: 'success'
                });
                this.loadData();
            }).catch(err => {
                this.$vs.loading.close();
                this.$vs.notify({
                    title: 'Error',
                    text: 'Failed to delete PO detail',
                    color: 'danger'
                });
                console.error(err);
            });
        }
    }
}
</script>

<style lang="scss" scoped>
.text-muted {
    color: #6c757d;
    font-size: 0.9rem;
}

.mb-4 {
    margin-bottom: 1.5rem;
}

.mb-2 {
    margin-bottom: 0.5rem;
}

.mt-2 {
    margin-top: 0.5rem;
}

.mr-4 {
    margin-right: 1rem;
}

.mt-4 {
    margin-top: 1.5rem;
}

.bg-light {
    background-color: #f8f9fa;
}

.border {
    border: 1px solid #dee2e6;
}

.rounded {
    border-radius: 0.25rem;
}

.p-3 {
    padding: 1rem;
}
</style>

